'use client';

import React, { useState } from 'react';
import { Eye, EyeClosed } from 'lucide-react';

type FormInputWithLabelProps = {
    id: string;
    name: string;
    label: string;
    placeholder?: string;
    type?: 'text' | 'email' | 'password' | 'tel';
    value?: string;
    maxLength?: number;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
    autoComplete?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export default function FormInputWithLabel({
    id,
    name,
    label,
    placeholder = ' ',
    type = 'text',
    value,
    maxLength = 200,
    inputMode,
    autoComplete = 'off',
    onChange,
}: FormInputWithLabelProps) {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword ? (isPasswordVisible ? 'text' : 'password') : type;

    // パスワード入力の場合、autocompleteを明示的に設定
    const autoCompleteValue = isPassword ? (autoComplete === 'off' ? 'new-password' : autoComplete) : autoComplete;

    return (
        <div className="flex w-full relative">
            <input
                type={inputType}
                id={id}
                name={name}
                inputMode={inputMode}
                maxLength={maxLength}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoComplete={autoCompleteValue}
                className="
                    font-notojp font-medium text-base text-color-base tracking-widest leading-normal
                    bg-color-inputform-g-bg
                    border border-color-white-base
                    focus:outline-none focus:border-color-inputform-selected focus:bg-color-white-base
                    autofill:shadow-[inset_0_0_0px_1000px_#F1F1F1]
                    w-full pt-6 pb-2 px-4 block
                    appearance-none peer
                "
            />
            <label
                htmlFor={id}
                className="
                    absolute font-notojp font-medium text-sm text-color-inputform-g-placeholder tracking-widest
                    duration-300 transform -translate-y-4 scale-75 top-4 md:top-5 z-10 origin-[0] left-4
                    peer-focus:text-color-inputform-selected peer-placeholder-shown:scale-100
                    peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4
                    cursor-text pointer-events-none select-none
                "
            >
                {label}
            </label>
            {isPassword && (
                <button
                    type="button"
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    className="absolute right-3 top-4"
                >
                    {isPasswordVisible ? (
                        <EyeClosed strokeWidth={1.75} size={25} className="text-color-inputform-g-placeholder" />
                    ) : (
                        <Eye strokeWidth={1.75} size={25} className="text-color-base" />
                    )}
                </button>
            )}
        </div>
    );
}
